import { array, option } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { useMemo } from 'react';
import { Option } from 'fp-ts/Option';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { CountryCode, FeatureFlag } from '@mews-commander/enums';
import {
    AccountingEnvironment,
    ApplicableBillTypeParameters,
    BillType,
    Counter,
    EnterpriseScopeCounterDiscriminator,
    useRepositoryQuery,
} from '@mews-commander/data-access';
import { useFeatureFlag } from '@mews/framework';
import { useCommanderSelector } from '@mews-commander/utils';
import { getAccountingEnvironmentByCode } from '@mews-commander/legacy-entities/src/ducks/accountingEnvironments';
import { CounterUseFor } from './components/CounterForm/formMapping';

export const useCanAddOrResetCounters = () => {
    const { accountingEnvironmentCode } = useCurrentEnterprise();

    const disallowedCountries = [CountryCode.France];

    return !disallowedCountries.includes(accountingEnvironmentCode as CountryCode);
};

export const convertUseForFormValueToApplicableBillType = (useFor: CounterUseFor[]): ApplicableBillTypeParameters => ({
    applyToBill: useFor.includes(CounterUseFor.bills),
    applyToInvoice: useFor.includes(CounterUseFor.invoices),
});

export const useParseNumberFormatFromString = (numberFormat?: string | null) =>
    useMemo<{ numberOfDigits: number; prefix: string | null; suffix: string | null } | null>(() => {
        if (!numberFormat) {
            return {
                numberOfDigits: 1,
                prefix: null,
                suffix: null,
            };
        }

        const matchResult = /(?<prefix>.+)?\{0:(?<numberOfDigits>0+)}(?<suffix>.+)?/.exec(numberFormat);

        if (!matchResult || !matchResult.groups) {
            return {
                numberOfDigits: 1,
                prefix: numberFormat,
                suffix: null,
            };
        }

        return {
            numberOfDigits: matchResult.groups.numberOfDigits?.length || 1,
            prefix: matchResult.groups.prefix || null,
            suffix: matchResult.groups.suffix || null,
        };
    }, [numberFormat]);

export const useGetCreditNoteCounters = () => {
    const { data } = useRepositoryQuery('counters', 'getAll', {});

    return pipe(
        data?.counters,
        option.fromNullable,
        option.map(
            array.filter((it) => it.data.discriminator === EnterpriseScopeCounterDiscriminator.CreditNoteBillCounter)
        ),
        option.fold(
            () => [],
            (items) => items
        )
    );
};

export const useIsCreditNoteCountersEnabled = () => {
    const { accountingEnvironmentCode } = useCurrentEnterprise();
    const environment = useCommanderSelector((state) =>
        getAccountingEnvironmentByCode(state, {
            code: accountingEnvironmentCode,
        })
    ) as AccountingEnvironment;

    const creditNoteCountersEnabled = useFeatureFlag(FeatureFlag.creditNoteCounterForItalyBasqueSpain);

    return !!environment?.isCreditNoteBillCounterRequired && creditNoteCountersEnabled;
};

export const getNumberFormatFromCreditNoteCounter = (data: Counter['data']) => {
    if (data.discriminator !== EnterpriseScopeCounterDiscriminator.CreditNoteBillCounter) return;

    return `${data.value.prefix || ''}${array.makeBy(data.value.numberOfDigits, (_) => 0).join('')}`;
};
export const getBillCounterValues = (counter: Counter) => {
    switch (counter.data.discriminator) {
        case EnterpriseScopeCounterDiscriminator.AvailabilityBlockCounter:
        case EnterpriseScopeCounterDiscriminator.FiscalCounter:
        case EnterpriseScopeCounterDiscriminator.RegistrationCardCounter:
        case EnterpriseScopeCounterDiscriminator.ServiceOrderCounter:
            return {
                title: '',
                billTypeCode: '',
                displayCid: false,
                creditNoteTitle: '',
            };

        case EnterpriseScopeCounterDiscriminator.AccountingCounter:
        case EnterpriseScopeCounterDiscriminator.BillPreviewCounter:
        case EnterpriseScopeCounterDiscriminator.ProformaCounter:
            return {
                title: counter.data.value.title,
                billTypeCode: '',
                displayCid: false,
                creditNoteTitle: '',
            };

        case EnterpriseScopeCounterDiscriminator.BillCounter:
        case EnterpriseScopeCounterDiscriminator.CorrectionBillCounter:
        case EnterpriseScopeCounterDiscriminator.PaymentConfirmationBillCounter:
            return {
                title: counter.data.value.title,
                billTypeCode: counter.data.value.billTypeCode,
                displayCid: counter.data.value.displayCid,
                creditNoteTitle: counter.data.value.creditNoteTitle ?? '',
            };

        default:
            throw new Error('Not supported Counter Type');
    }
};
export const useIsNonRegionalCreditNoteCounterEnabled = () => {
    const nonRegional = useFeatureFlag(FeatureFlag.creditNoteBillCounter);
    const regional = useFeatureFlag(FeatureFlag.creditNoteCounterForItalyBasqueSpain);

    return nonRegional && !regional;
};
export const findCounterByBillType = (billType: Option<BillType>) => (counter: Counter) =>
    pipe(
        option.some(counter),
        option.chain(({ data }) =>
            data?.discriminator === EnterpriseScopeCounterDiscriminator.CreditNoteBillCounter
                ? option.some(data.value.applicableBillType)
                : option.none
        ),
        option.bind('cleanBillType', () => billType),
        option.chain(({ applyToBill, applyToInvoice, cleanBillType }) =>
            (applyToBill && cleanBillType === BillType.Receipt) ||
            (applyToInvoice && cleanBillType === BillType.Invoice) ||
            (applyToInvoice && applyToBill)
                ? option.some(true)
                : option.none
        ),
        option.isSome
    );
